/**
 * Structured Data Schema Generators
 * 
 * Provides JSON-LD schema generators for enhanced search visibility:
 * - FAQPage for FAQ content
 * - BreadcrumbList for navigation paths
 * - SpeakableSpecification for voice assistant content
 * - Article, Organization, etc.
 */

const SITE_URL = "https://www.thezoomagency.com";
const SITE_NAME = "The Zoom Agency";

// ============================================
// ORGANIZATION SCHEMA
// ============================================

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ["Zoom Agency", "The Zoom Agency UK"],
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo.png`,
    width: 512,
    height: 512
  },
  image: `${SITE_URL}/og-image.png`,
  description: "The Zoom Agency is a creative and performance marketing consultancy established in 2013. We partner with start-ups to enterprises, helping CMOs and CEOs build marketing that delivers long-term brand and commercial impact.",
  foundingDate: "2013",
  foundingLocation: {
    "@type": "Place",
    name: "United Kingdom"
  },
  sameAs: [
    "https://www.linkedin.com/company/the-zoom-agency",
    "https://www.facebook.com/thezoomagency",
    "https://twitter.com/thezoomagency",
    "https://www.instagram.com/thezoomagency"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: "+44 203 488 3090",
    email: "go@thezoomagency.com",
    availableLanguage: ["English", "Spanish", "French", "German"]
  },
  address: {
    "@type": "PostalAddress",
    addressCountry: "GB"
  },
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
    { "@type": "Place", name: "Europe" }
  ],
  knowsAbout: [
    "Marketing Strategy",
    "Brand Development",
    "Performance Marketing",
    "Creative Design",
    "Digital Marketing",
    "B2B Marketing",
    "B2C Marketing",
    "Fractional CMO",
    "Paid Media",
    "Social Media Marketing"
  ]
};

// ============================================
// WEBSITE SCHEMA
// ============================================

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  description: "Creative and performance marketing consultancy established in 2013.",
  publisher: {
    "@id": `${SITE_URL}/#organization`
  },
  inLanguage: "en",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/insights?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

// ============================================
// FAQ SCHEMA
// ============================================

export interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Generate FAQPage structured data
 * @param faqs Array of question/answer pairs
 * @param pageUrl The canonical URL of the FAQ page
 */
export function generateFAQSchema(faqs: FAQItem[], pageUrl?: string) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer.replace(/\n/g, " ").trim()
      }
    }))
  };

  if (pageUrl) {
    schema.url = pageUrl.startsWith("http") ? pageUrl : `${SITE_URL}${pageUrl}`;
  }

  return schema;
}

// ============================================
// BREADCRUMB SCHEMA
// ============================================

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Generate BreadcrumbList structured data
 * @param items Array of breadcrumb items (from root to current page)
 */
export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`
    }))
  };
}

/**
 * Common breadcrumb paths
 */
export const breadcrumbPaths = {
  home: { name: "Home", url: "/" },
  work: { name: "Work", url: "/work" },
  services: { name: "Services", url: "/services" },
  insights: { name: "Insights", url: "/insights" },
  faq: { name: "FAQ", url: "/faq" },
  whoWeWorkWith: { name: "Who We Work With", url: "/who-we-work-with" },
  whoWeReportTo: { name: "Who We Report To", url: "/who-we-report-to" },
};

// ============================================
// SPEAKABLE SCHEMA
// ============================================

/**
 * Generate SpeakableSpecification for voice assistant content
 * @param cssSelectors CSS selectors for speakable content
 * @param pageUrl The canonical URL of the page
 */
export function generateSpeakableSchema(cssSelectors: string[], pageUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: pageUrl.startsWith("http") ? pageUrl : `${SITE_URL}${pageUrl}`,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: cssSelectors
    }
  };
}

/**
 * Generate combined WebPage schema with speakable specification
 * For service pages with answer-first content
 */
export function generateServicePageSchema(options: {
  name: string;
  description: string;
  url: string;
  speakableSelectors?: string[];
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: options.name,
    description: options.description,
    url: options.url.startsWith("http") ? options.url : `${SITE_URL}${options.url}`,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL
    },
    provider: {
      "@type": "Organization",
      name: SITE_NAME
    }
  };

  if (options.speakableSelectors && options.speakableSelectors.length > 0) {
    schema.speakable = {
      "@type": "SpeakableSpecification",
      cssSelector: options.speakableSelectors
    };
  }

  return schema;
}

// ============================================
// ARTICLE SCHEMA
// ============================================

/**
 * Parse human-readable date to ISO 8601 format
 * e.g., "13 February 2026" -> "2026-02-13"
 */
function parseToISO(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toISOString().split("T")[0];
  } catch {
    return dateStr;
  }
}

/**
 * Generate NewsArticle structured data (Google News compatible)
 */
export function generateArticleSchema(article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  slug: string;
  authorName?: string;
  authorLinkedIn?: string;
}) {
  const isoDatePublished = parseToISO(article.datePublished);
  const isoDateModified = article.dateModified ? parseToISO(article.dateModified) : isoDatePublished;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.description,
    image: article.image.startsWith("http") ? article.image : `${SITE_URL}${article.image}`,
    datePublished: isoDatePublished,
    dateModified: isoDateModified,
    isAccessibleForFree: true,
    author: article.authorName ? {
      "@type": "Person",
      name: article.authorName,
      ...(article.authorLinkedIn && { sameAs: [article.authorLinkedIn] }),
      worksFor: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL
      }
    } : {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`
      }
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/insights/${article.slug}`
    }
  };
}

// ============================================
// CASE STUDY SCHEMA
// ============================================

/**
 * Generate Case Study structured data
 */
export function generateCaseStudySchema(caseStudy: {
  title: string;
  description: string;
  image: string;
  sector: string;
  slug: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: caseStudy.title,
    description: caseStudy.description,
    image: caseStudy.image.startsWith("http") ? caseStudy.image : `${SITE_URL}${caseStudy.image}`,
    creator: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL
    },
    about: {
      "@type": "Thing",
      name: caseStudy.sector
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/work/${caseStudy.slug}`
    }
  };
}

// ============================================
// SERVICES SCHEMA
// ============================================

export const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Marketing Consultancy",
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL
  },
  areaServed: {
    "@type": "Place",
    name: "Worldwide"
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Marketing Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Brand Strategy",
          description: "Brand positioning, messaging frameworks, and go-to-market strategies",
          url: `${SITE_URL}/services/brand-strategy`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Creative Execution",
          description: "Campaign concepts, visual systems, and digital experiences",
          url: `${SITE_URL}/services/creative-execution`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Paid Media",
          description: "Performance marketing across Google, Meta, TikTok, and LinkedIn",
          url: `${SITE_URL}/services/paid-media`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Content Creation",
          description: "Video, photography, copy, and design for campaigns",
          url: `${SITE_URL}/services/content-creation`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Social Media Marketing",
          description: "Platform strategy, content planning, and community management",
          url: `${SITE_URL}/services/social-media-marketing`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Influencer Marketing",
          description: "Creator partnerships and UGC campaigns",
          url: `${SITE_URL}/services/influencer-marketing`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Graphic Design",
          description: "Campaign systems, templates, ad creative and handover packs",
          url: `${SITE_URL}/services/graphic-design`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Web Development",
          description: "High-converting websites on WordPress, Shopify, Webflow, and custom builds",
          url: `${SITE_URL}/services/web-development`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Ongoing Support",
          description: "Always-on marketing support across brand, creative, and performance",
          url: `${SITE_URL}/services/ongoing-support`
        }
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Superintelligence Readiness",
          description: "Superintelligence readiness audits, workflow integration, and team training for marketing teams embracing superintelligence",
          url: `${SITE_URL}/services/superintelligence-readiness`
        }
      }
    ]
  }
};

// ============================================
// ITEM LIST SCHEMA (for carousel results)
// ============================================

/**
 * Generate ItemList structured data for service pages
 */
export function generateItemListSchema(items: Array<{ name: string; url: string; description?: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      ...(item.description ? { description: item.description } : {})
    }))
  };
}

// ============================================
// VIDEO SCHEMA
// ============================================

/**
 * Generate VideoObject structured data for video content
 */
export function generateVideoSchema(video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  embedUrl?: string;
  uploadDate: string;
  duration: string; // ISO 8601 duration format (e.g., "PT30S" for 30 seconds)
}) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl.startsWith("http") ? video.thumbnailUrl : `${SITE_URL}${video.thumbnailUrl}`,
    contentUrl: video.contentUrl.startsWith("http") ? video.contentUrl : `${SITE_URL}${video.contentUrl}`,
    embedUrl: video.embedUrl ? (video.embedUrl.startsWith("http") ? video.embedUrl : `${SITE_URL}${video.embedUrl}`) : undefined,
    uploadDate: video.uploadDate,
    duration: video.duration,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`
      }
    }
  };
}

// ============================================
// PROFESSIONAL SERVICE SCHEMA (for main pages)
// ============================================

export const professionalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#service`,
  name: SITE_NAME,
  description: "Full-service marketing consultancy combining creative excellence with performance discipline. Fractional CMO services, brand strategy, paid media, and creative execution for startups to enterprises.",
  url: SITE_URL,
  image: `${SITE_URL}/og-image.png`,
  priceRange: "$$-$$$",
  email: "go@thezoomagency.com",
  address: {
    "@type": "PostalAddress",
    addressCountry: "GB"
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 51.5074,
    longitude: -0.1278
  },
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
    { "@type": "Place", name: "Europe" }
  ],
  parentOrganization: { "@id": `${SITE_URL}/#organization` },
  knowsAbout: [
    "Marketing Strategy",
    "Brand Strategy",
    "Performance Marketing",
    "Paid Media",
    "Creative Direction",
    "Content Marketing",
    "Social Media Marketing",
    "Web Development",
    "Fractional CMO"
  ],
  hasOfferCatalog: servicesSchema.hasOfferCatalog
};

// ============================================
// PERSON AUTHOR SCHEMA
// ============================================

/**
 * Generate Person author schema for Superintelligence Overview entity resolution.
 * Named authors with sameAs LinkedIn URLs are the strongest AIO signal.
 */
export function generatePersonAuthorSchema(author: {
  name: string;
  url?: string;
  sameAs?: string[];
  jobTitle?: string;
}) {
  return {
    "@type": "Person",
    name: author.name,
    ...(author.url && { url: author.url }),
    ...(author.jobTitle && { jobTitle: author.jobTitle }),
    ...(author.sameAs && author.sameAs.length > 0 && { sameAs: author.sameAs }),
    worksFor: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL
    }
  };
}

// ============================================
// HELPER: Combine multiple schemas
// ============================================

/**
 * Combine multiple schemas into a single array for JSON-LD
 */
export function combineSchemas(...schemas: object[]) {
  return schemas;
}

// ============================================
// HOWTO SCHEMA (canonical, with @context)
// ============================================

export interface HowToStepItem {
  label: string;
  description: string;
}

export function generateHowToSchemaFull(options: {
  name: string;
  description: string;
  steps: HowToStepItem[];
  totalTime?: string;
  url?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: options.name,
    description: options.description,
    ...(options.totalTime && { totalTime: options.totalTime }),
    ...(options.url && { url: options.url.startsWith("http") ? options.url : `${SITE_URL}${options.url}` }),
    step: options.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.label,
      text: s.description
    }))
  };
}

// ============================================
// QUOTATION SCHEMA (testimonials in superintelligence overviews)
// ============================================

export interface QuotationItem {
  text: string;
  author: string;
  authorRole?: string;
  about?: string;
}

export function generateQuotationListSchema(quotes: QuotationItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Client Testimonials",
    itemListElement: quotes.map((q, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Quotation",
        text: q.text,
        creator: {
          "@type": "Person",
          name: q.author,
          ...(q.authorRole && { jobTitle: q.authorRole })
        },
        ...(q.about && { about: { "@type": "Organization", name: q.about } })
      }
    }))
  };
}

// ============================================
// BEN WALKER — FOUNDER PERSON ENTITY
// ============================================

export const benWalkerPersonSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/#ben-walker`,
  name: "Ben Walker",
  jobTitle: "Founder & Strategy Director",
  worksFor: { "@id": `${SITE_URL}/#organization` },
  url: SITE_URL,
  sameAs: [
    "https://www.linkedin.com/in/benwalkermarketing/"
  ],
  knowsAbout: [
    "Marketing Strategy",
    "Brand Strategy",
    "Performance Marketing",
    "Fractional CMO",
    "Superintelligence-accelerated Marketing",
    "Aesthetics Industry Marketing",
    "Luxury Brand Marketing",
    "B2B Marketing",
    "Paid Media Strategy"
  ],
  hasOccupation: {
    "@type": "Occupation",
    name: "Marketing Strategy Director",
    occupationLocation: { "@type": "Country", name: "United Kingdom" }
  },
  description: "Founder of The Zoom Agency with 25+ years of experience leading marketing for luxury, hospitality, B2B technology and aesthetics brands across the UK, US and Europe."
};

// ============================================
// SERVICE OFFER SCHEMA (with offers + audience)
// ============================================

export function generateServiceOfferSchema(options: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  audience?: string[];
  priceCurrency?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: options.name,
    description: options.description,
    ...(options.serviceType && { serviceType: options.serviceType }),
    url: options.url.startsWith("http") ? options.url : `${SITE_URL}${options.url}`,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: [
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Country", name: "United States" },
      { "@type": "Place", name: "Europe" }
    ],
    ...(options.audience && {
      audience: {
        "@type": "Audience",
        audienceType: options.audience.join(", ")
      }
    }),
    offers: {
      "@type": "Offer",
      priceCurrency: options.priceCurrency || "GBP",
      availability: "https://schema.org/InStock",
      url: options.url.startsWith("http") ? options.url : `${SITE_URL}${options.url}`
    }
  };
}
